import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";

import type {
  AIResponse,
  CalendarEvent,
  CalendarView,
  DastRunDetail,
  DastRunSummary,
  DastTokenBucket,
  Finding,
  FindingFilters,
  FindingTimelineEvent,
  Note,
  PipelineSummary,
  ProductSummary,
  Project,
  ProjectVersionType,
  Severity,
  WorkItemLink,
  WorkItemStatusCategory,
} from "../types";
import { fetchJson, normalizeList } from "./api";
import { type FindingsFilterUrlState, toFindingsApiFilters } from "./findingsFilterUrl";
import { getRoute } from "./routes";

type WorkItemLinkApi = {
  id: number;
  external_key: string;
  external_url: string;
  title: string;
  status_category: string;
  provider_name: string | null;
  provider_type: string | null;
  provider: number | null;
};

type FindingApi = {
  id: number;
  title: string;
  severity: "Critical" | "High" | "Medium" | "Low" | "Info";
  active: boolean;
  product?: number | null;
  project_id?: number | null;
  date?: string;
  created?: string;
  project_version?: string | null;
  project_version_type?: ProjectVersionType | null;
  file_path?: string | null;
  line?: number | null;
  description?: string | null;
  cwe?: number | null;
  tags?: string[];
  project_tags?: string[];
  test?: number;
  risk_accepted?: boolean;
  under_review?: boolean;
  is_mitigated?: boolean;
  false_p?: boolean;
  out_of_scope?: boolean;
  duplicate?: boolean;
  found_by?: number[];
  last_status_update?: string;
  is_regression?: boolean;
  finding_meta?: { name: string; value: string }[];
  work_items?: WorkItemLinkApi[];
  dynamic_finding?: boolean;
  affected_endpoints?: string[];
  steps_to_reproduce?: string | null;
  impact?: string | null;
  mitigation?: string | null;
  param?: string | null;
  payload?: string | null;
  cvssv3?: string | null;
  cvssv3_score?: number | null;
  references?: string | null;
};

type AistProjectApi = {
  id: number;
  product_id: number;
  product_name: string;
  organization_id?: number | null;
  organization_name?: string | null;
};

type ProductSummaryApi = {
  project_id: number;
  product_id: number;
  product_name: string;
  organization_id?: number | null;
  tags: string[];
  status: "active" | "inactive";
  findings_total: number;
  findings_active: number;
  severity: Record<string, number>;
  risk: {
    risk_accepted: number;
    under_review: number;
    mitigated: number;
  };
  risk_score?: {
    score: number;
    label: "critical" | "high" | "medium" | "low";
  } | null;
  last_pipeline?: {
    id?: string | null;
    status?: string | null;
    updated?: string | null;
  };
  last_sync?: string | null;
};

type ProjectMetaApi = {
  versions: { id: string; label: string; version_type?: ProjectVersionType }[];
};

type PipelineApi = {
  id: string;
  execution_type: "SAST" | "DAST" | "MANUAL_IMPORT";
  status: string;
  run_task_id: string | null;
  external_run_id: string | null;
  external_log_cursor: number;
  external_execution_outcome: "" | "RUNNING" | "STOP_PENDING" | "TERMINAL" | "CANCELLED_BEFORE_START" | "UNREACHABLE";
  dast_outcome_code: import("./dastNarrative").DastOutcomeCode | null;
  external_execution_deadline: string | null;
  external_cancel_requested_at: string | null;
  response_from_ai: unknown;
  created: string;
  updated: string;
};

type AIFindingResponseApi = {
  pipeline_id: string;
  finding_id: number;
  verdict: "true_positive" | "false_positive" | "uncertain";
  title: string;
  reasoning: string;
  epssScore?: number | null;
  impactScore?: number | null;
  exploitabilityScore?: number | null;
  uncertaintyLevel?: number | null;
  uncertaintySpread?: number | null;
  exploitCodeMaturity?: string;
  references?: string[];
  fix?: import("../types").AiFix | null;
  created?: string;
};

type DastRunSummaryApi = {
  run_id: string;
  run_type: string | null;
  coverage_unit: string | null;
  discovered: number | null;
  reachable: number | null;
  analysed: number | null;
  planned: number | null;
  beyond_plan: number | null;
  total_tokens: number | null;
  model_calls: number | null;
};

type DastRunDetailApi = DastRunSummaryApi & {
  target_id: string;
  stand_id: string;
  product_family: string | null;
  tier: string | null;
  target_host: string | null;
  scan_started: string | null;
  scan_finished: string | null;
  duration_seconds: number | null;
  analysed_names: string[] | null;
  beyond_plan_names: string[] | null;
  tokens: DastRunDetail["tokens"];
  token_by_phase: DastTokenBucket[] | null;
  token_by_agent_type: DastTokenBucket[] | null;
  agents: number | null;
  token_accounting_consistent: boolean | null;
};

type PipelineSummaryApi = {
  id: string;
  execution_type: "SAST" | "DAST" | "MANUAL_IMPORT";
  status: string;
  dast_outcome_code?: import("./dastNarrative").DastOutcomeCode | null;
  dast_run?: DastRunSummaryApi | null;
  project_id: number;
  product_id: number;
  product_name: string;
  started?: string | null;
  created?: string | null;
  updated?: string | null;
  branch?: string | null;
  commit?: string | null;
  findings?: number;
  actions?: Array<{
    source?: string | null;
    type?: string | null;
    status?: string | null;
    updated?: string | null;
  }>;
};

type CalendarEventApi = {
  id: string;
  event_type: CalendarEvent["eventType"];
  title: string;
  start: string;
  end?: string | null;
  is_all_day: boolean;
  is_aggregated: boolean;
  count: number;
  is_future: boolean;
  color_variant: string;
  summary: Record<string, unknown>;
  link?: string | null;
};

type CalendarEventsApiResponse = {
  events: CalendarEventApi[];
};

type FindingTimelineEventApi = {
  id: string;
  event_type: FindingTimelineEvent["eventType"];
  happened_at: string;
  finding_id: number;
  title: string;
  severity: string;
  project_ids?: number[];
  processed_reason?: string | null;
  owner?: string | null;
  details?: string | null;
  link: string;
};

type FindingTimelineApiResponse = {
  events: FindingTimelineEventApi[];
};

const DAY_MS = 24 * 60 * 60 * 1000;
const FINDING_HISTORY_RANGE_DAYS = 3650;
const FINDING_TIMELINE_LIMIT = 300;
const FINDING_TIMELINE_EVENT_TYPES: FindingTimelineEvent["eventType"][] = [
  "finding_created",
  "finding_processed",
  "finding_note_added",
];

type ListResponse<T> = { results?: T[]; count?: number; next?: string | null; previous?: string | null };

export function buildFindingsParams(
  filters: FindingFilters,
  pagination?: { limit?: number; offset?: number },
): URLSearchParams {
  return new URLSearchParams({
    ...(pagination?.limit !== undefined ? { limit: String(pagination.limit) } : {}),
    ...(pagination?.offset !== undefined ? { offset: String(pagination.offset) } : {}),
    ...(filters.projectId ? { project_id: String(filters.projectId) } : {}),
    ...(filters.pipelineId ? { pipeline_id: filters.pipelineId } : {}),
    ...(filters.title ? { title: filters.title } : {}),
    ...(filters.createdGte ? { created_gte: filters.createdGte } : {}),
    ...(filters.createdLte ? { created_lte: filters.createdLte } : {}),
    ...(filters.statusUpdatedGte ? { status_updated_gte: filters.statusUpdatedGte } : {}),
    ...(filters.statusUpdatedLte ? { status_updated_lte: filters.statusUpdatedLte } : {}),
    ...(filters.processedGte ? { processed_gte: filters.processedGte } : {}),
    ...(filters.processedLte ? { processed_lte: filters.processedLte } : {}),
    ...(filters.mitigatedGte ? { mitigated_gte: filters.mitigatedGte } : {}),
    ...(filters.mitigatedLte ? { mitigated_lte: filters.mitigatedLte } : {}),
    ...(filters.workItemLinkedGte ? { work_item_linked_gte: filters.workItemLinkedGte } : {}),
    ...(filters.workItemLinkedLte ? { work_item_linked_lte: filters.workItemLinkedLte } : {}),
    ...(filters.projectVersion ? { project_version: filters.projectVersion } : {}),
    ...(filters.file ? { file: filters.file } : {}),
    ...(filters.aiStatus ? { ai_status: filters.aiStatus } : {}),
    ...(filters.severities?.length ? { severity: filters.severities.join(",") } : {}),
    ...(filters.status ? { active: filters.status === "enabled" ? "true" : "false" } : {}),
    ...(filters.riskStates?.includes("risk_accepted") ? { risk_accepted: "true" } : {}),
    ...(filters.riskStates?.includes("under_review") ? { under_review: "true" } : {}),
    ...(filters.riskStates?.includes("mitigated") ? { is_mitigated: "true" } : {}),
    ...(filters.cwe ? { cwe: filters.cwe } : {}),
    ...(filters.tags?.length
      ? { [filters.tagMatchMode === "all" ? "tags__and" : "tags"]: filters.tags.map((tag) => tag.trim()).filter(Boolean).join(",") }
      : {}),
    ...(filters.excludedTags?.length
      ? { not_tags: filters.excludedTags.map((tag) => tag.trim()).filter(Boolean).join(",") }
      : {}),
    ...(filters.ordering ? { ordering: filters.ordering } : {}),
    ...(filters.workItemStatus ? { work_item_status: filters.workItemStatus } : {}),
  });
}

/** API query for a Findings filter state: the Findings list and the dashboard summary share it. */
export function buildFindingsQuery(state: FindingsFilterUrlState): URLSearchParams {
  return buildFindingsParams(toFindingsApiFilters(state));
}

function mapFindingApiToUi(item: FindingApi): Finding {
  const normalizedLine = typeof item.line === "number" ? item.line : null;
  return {
    sourceFileLink: item.finding_meta?.find((meta) => meta.name === "sourcefile_link")?.value,
    id: item.id,
    title: item.title,
    severity: item.severity,
    active: item.active,
    isMitigated: item.is_mitigated ?? false,
    riskAccepted: item.risk_accepted ?? false,
    falsePositive: item.false_p ?? false,
    outOfScope: item.out_of_scope ?? false,
    duplicate: item.duplicate ?? false,
    product: String(item.product ?? ""),
    projectId: item.project_id ?? undefined,
    date: item.date,
    createdAt: item.created ?? item.date,
    projectVersion: item.project_version ?? undefined,
    projectVersionType: item.project_version_type ?? undefined,
    filePath: item.file_path ?? "",
    line: normalizedLine,
    tool: "",
    description: item.description ?? undefined,
    cwe: item.cwe ?? null,
    tags: normalizeTags(item.tags),
    projectTags: normalizeTags(item.project_tags),
    testId: item.test ?? null,
    lastStatusUpdate: item.last_status_update ?? undefined,
    isRegression: item.is_regression ?? false,
    workItems: item.work_items?.map((wi): WorkItemLink => ({
      id: wi.id,
      externalKey: wi.external_key,
      externalUrl: wi.external_url,
      title: wi.title,
      statusCategory: (wi.status_category as WorkItemLink["statusCategory"]) ?? "UNKNOWN",
      providerName: wi.provider_name ?? null,
      providerType: wi.provider_type ?? null,
      provider: wi.provider ?? null,
    })) ?? [],
    riskStates: [
      item.risk_accepted ? "risk_accepted" : null,
      item.under_review ? "under_review" : null,
      item.is_mitigated ? "mitigated" : null,
    ].filter(Boolean) as Finding["riskStates"],
    dynamicFinding: item.dynamic_finding ?? false,
    affectedEndpoints: item.affected_endpoints ?? [],
    stepsToReproduce: item.steps_to_reproduce ?? undefined,
    impact: item.impact ?? undefined,
    mitigation: item.mitigation ?? undefined,
    param: item.param ?? undefined,
    payload: item.payload ?? undefined,
    cvssv3: item.cvssv3 ?? undefined,
    cvssv3Score: item.cvssv3_score ?? undefined,
    references: item.references ?? undefined,
  };
}

function normalizeTags(raw?: FindingApi["tags"]) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && "name" in item) {
          return String((item as { name?: string }).name ?? "");
        }
        return "";
      })
      .map((tag) => tag.trim())
      .filter(Boolean);
  }
  if (typeof raw === "string") {
    return raw.split(",").map((tag) => tag.trim()).filter(Boolean);
  }
  return [];
}

export function useProjects() {
  return useQuery({
    queryKey: ["aist-projects"],
    queryFn: async () => {
      const payload = await fetchJson<ListResponse<AistProjectApi>>(getRoute("projects_list_url"));
      return normalizeList(payload).map((item): Project => ({
        id: item.id,
        productId: item.product_id,
        name: item.product_name,
        organizationId: item.organization_id ?? null,
        organizationName: item.organization_name ?? null,
      }));
    },
  });
}

export function useProductSummaries() {
  return useQuery({
    queryKey: ["product-summaries"],
    queryFn: async () => {
      const payload = await fetchJson<ListResponse<ProductSummaryApi>>(getRoute("product_summary_url"));
      return normalizeList(payload).map((item): ProductSummary => ({
        projectId: item.project_id,
        productId: item.product_id,
        organizationId: item.organization_id ?? null,
        name: item.product_name,
        tags: item.tags ?? [],
        status: item.status,
        findingsTotal: item.findings_total,
        findingsActive: item.findings_active,
        severity: {
          Critical: item.severity?.Critical ?? 0,
          High: item.severity?.High ?? 0,
          Medium: item.severity?.Medium ?? 0,
          Low: item.severity?.Low ?? 0,
          Info: item.severity?.Info ?? 0,
        },
        risk: {
          riskAccepted: item.risk?.risk_accepted ?? 0,
          underReview: item.risk?.under_review ?? 0,
          mitigated: item.risk?.mitigated ?? 0,
        },
        riskScore: item.risk_score ?? undefined,
        lastPipeline: item.last_pipeline ?? null,
        lastSync: item.last_sync ?? null,
      }));
    },
  });
}

export function useFindings(projectId?: number) {
  const filters: FindingFilters = projectId ? { projectId } : {};
  return useFindingsWithFilters(filters);
}

export function useFindingsPage(filters: FindingFilters) {
  const limit = filters.limit ?? 50;
  const offset = filters.offset ?? 0;
  return useQuery({
    queryKey: ["findings-page", filters],
    queryFn: async () => {
      const params = buildFindingsParams(filters, { limit, offset });
      const payload = await fetchJson<ListResponse<FindingApi>>(
        `${getRoute("findings_list_url")}?${params.toString()}`,
      );
      const items = normalizeList(payload).map(mapFindingApiToUi);
      return {
        items,
        count: payload.count ?? items.length,
      };
    },
  });
}

export function useFindingsWithFilters(filters: FindingFilters) {
  const limit = filters.limit ?? 50;
  return useInfiniteQuery({
    queryKey: ["findings", filters],
    queryFn: async ({ pageParam = 0 }) => {
      const params = buildFindingsParams(filters, { limit, offset: pageParam });
      const payload = await fetchJson<ListResponse<FindingApi>>(
        `${getRoute("findings_list_url")}?${params.toString()}`,
      );
      const items = normalizeList(payload).map(mapFindingApiToUi);
      return {
        items,
        count: payload.count ?? items.length,
        next: payload.next ?? null,
        previous: payload.previous ?? null,
        nextOffset: payload.next ? pageParam + limit : null,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
  });
}

export function useFinding(findingId?: number) {
  return useQuery({
    queryKey: ["finding", findingId],
    queryFn: async () => {
      if (!findingId) return null;
      const item = await fetchJson<FindingApi>(
        getRoute("finding_detail_url", { id: findingId }),
      );
      return mapFindingApiToUi(item) satisfies Finding;
    },
    enabled: Boolean(findingId),
  });
}

export function useFindingProjectVersion(findingId?: number) {
  return useQuery({
    queryKey: ["finding-project-version", findingId],
    queryFn: async () => {
      if (!findingId) return undefined;
      const params = new URLSearchParams({
        limit: "1",
        id: String(findingId),
      });
      const payload = await fetchJson<ListResponse<FindingApi>>(
        `${getRoute("findings_list_url")}?${params.toString()}`,
      );
      const item = normalizeList(payload)[0];
      if (!item?.project_version) return undefined;
      return {
        projectId: item.project_id ?? undefined,
        version: item.project_version,
        versionType: item.project_version_type ?? undefined,
      };
    },
    enabled: Boolean(findingId),
  });
}

type TestDetailApi = {
  id: number;
  engagement?: number;
  engagement_id?: number;
  product?: number;
  product_id?: number;
};

export function useTestEngagement(testId?: number | null) {
  return useQuery({
    queryKey: ["test", testId],
    queryFn: async () => {
      if (!testId) return null;
      const payload = await fetchJson<TestDetailApi>(getRoute("test_detail_url", { id: testId }));
      return payload.engagement_id ?? payload.engagement ?? null;
    },
    enabled: Boolean(testId),
    staleTime: 10 * 60 * 1000,
  });
}

type EngagementDetailApi = {
  id: number;
  product?: number;
  product_id?: number;
};

export function useEngagementProduct(engagementId?: number | null) {
  return useQuery({
    queryKey: ["engagement", engagementId],
    queryFn: async () => {
      if (!engagementId) return null;
      const payload = await fetchJson<EngagementDetailApi>(
        getRoute("engagement_detail_url", { id: engagementId }),
      );
      return payload.product_id ?? payload.product ?? null;
    },
    enabled: Boolean(engagementId),
    staleTime: 10 * 60 * 1000,
  });
}

export function useFindingNotes(findingId?: number) {
  return useQuery({
    queryKey: ["finding-notes", findingId],
    queryFn: async () => {
      if (!findingId) return [];
      const payload = await fetchJson<Note[]>(
        getRoute("finding_notes_url", { finding_id: findingId }),
      );
      return payload ?? [];
    },
    enabled: Boolean(findingId),
  });
}

export function useProjectMeta(projectId?: number) {
  return useQuery({
    queryKey: ["project-meta", projectId],
    queryFn: async () => {
      if (!projectId) return null;
      return fetchJson<ProjectMetaApi>(
        getRoute("project_meta_url", { project_id: projectId }),
      );
    },
    enabled: Boolean(projectId),
  });
}

export function usePipelines(projectId?: number) {
  return useQuery({
    queryKey: ["pipelines", projectId],
    queryFn: async () => {
      if (!projectId) return [];
      const params = new URLSearchParams({
        limit: "5",
        ordering: "-created",
        project_id: String(projectId),
      });
      const payload = await fetchJson<ListResponse<PipelineApi>>(
        `${getRoute("pipelines_list_url")}?${params.toString()}`,
      );
      return normalizeList(payload);
    },
    enabled: Boolean(projectId),
  });
}

export function useAiFindingResponses(
  projectId?: number,
  pipelineId?: string,
  findingIds?: number[],
) {
  return useQuery({
    queryKey: ["ai-finding-responses", projectId, pipelineId, findingIds],
    queryFn: async () => {
      if (!projectId && !findingIds?.length) return new Map<number, AIResponse>();

      const params = new URLSearchParams({
        ...(projectId ? { project_id: String(projectId) } : {}),
        ...(pipelineId ? { pipeline_id: pipelineId } : {}),
      });
      if (findingIds?.length) {
        params.set("finding_ids", findingIds.join(","));
      }

      const payload = await fetchJson<AIFindingResponseApi[]>(
        `${getRoute("ai_finding_responses_url")}?${params.toString()}`,
      );
      const map = new Map<number, AIResponse>();
      for (const item of payload ?? []) {
        map.set(item.finding_id, {
          verdict: item.verdict,
          title: item.title ?? "",
          reasoning: item.reasoning ?? "AI response available.",
          epssScore: item.epssScore ?? undefined,
          impactScore: item.impactScore ?? undefined,
          exploitabilityScore: item.exploitabilityScore ?? undefined,
          uncertaintyLevel: item.uncertaintyLevel ?? undefined,
          uncertaintySpread: item.uncertaintySpread ?? undefined,
          exploitCodeMaturity: item.exploitCodeMaturity ?? undefined,
          references: item.references ?? [],
          fix: item.fix ?? undefined,
          pipelineId: item.pipeline_id,
        });
      }
      return map;
    },
    enabled: Boolean(projectId || findingIds?.length),
  });
}

type PipelineSummaryFilters = {
  projectId?: number;
  status?: string;
  createdGte?: string;
  createdLte?: string;
  search?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
};

export function usePipelineSummaries(filters: PipelineSummaryFilters) {
  return useQuery({
    queryKey: ["pipelines-summary", filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        ...(filters.projectId ? { project_id: String(filters.projectId) } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.createdGte ? { created_gte: filters.createdGte } : {}),
        ...(filters.createdLte ? { created_lte: filters.createdLte } : {}),
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.ordering ? { ordering: filters.ordering } : {}),
        limit: String(filters.limit ?? 50),
        offset: String(filters.offset ?? 0),
      });
      const payload = await fetchJson<ListResponse<PipelineSummaryApi>>(
        `${getRoute("pipelines_summary_url")}?${params.toString()}`,
      );
      return {
        items: normalizeList(payload).map((item): PipelineSummary => ({
          id: item.id,
          executionType: item.execution_type,
          status: item.status,
          dastOutcomeCode: item.dast_outcome_code ?? null,
          dastRun: normalizeDastRunSummary(item.dast_run),
          projectId: item.project_id,
          productId: item.product_id,
          productName: item.product_name,
          started: item.started ?? null,
          created: item.created ?? null,
          updated: item.updated ?? null,
          branch: item.branch ?? null,
          commit: item.commit ?? null,
          findings: item.findings ?? 0,
          actions: item.actions ?? [],
        })),
        count: payload.count ?? 0,
      };
    },
  });
}

function normalizeDastRunSummary(api?: DastRunSummaryApi | null): DastRunSummary | null {
  if (!api) return null;
  return {
    runId: api.run_id,
    runType: api.run_type,
    coverageUnit: api.coverage_unit,
    discovered: api.discovered,
    reachable: api.reachable,
    analysed: api.analysed,
    planned: api.planned,
    beyondPlan: api.beyond_plan,
    totalTokens: api.total_tokens,
    modelCalls: api.model_calls,
  };
}

/**
 * Coverage and token usage of the DAST report accepted onto a pipeline, including the analysed
 * inventory. Fetched only when a card is expanded and never refetched: a finished run's report
 * cannot change, and the inventory is too large to carry on the list query.
 */
export function usePipelineDastRun(pipelineId: string | null) {
  return useQuery({
    queryKey: ["pipeline-dast-run", pipelineId],
    queryFn: async () => {
      const payload = await fetchJson<{ dast_run: DastRunDetailApi | null }>(
        getRoute("pipeline_dast_run_url", { pipeline_id: pipelineId! }),
      );
      const api = payload.dast_run;
      if (!api) return null;
      return {
        ...(normalizeDastRunSummary(api) as DastRunSummary),
        targetId: api.target_id,
        standId: api.stand_id,
        productFamily: api.product_family,
        tier: api.tier,
        targetHost: api.target_host,
        scanStarted: api.scan_started,
        scanFinished: api.scan_finished,
        durationSeconds: api.duration_seconds,
        analysedNames: api.analysed_names,
        beyondPlanNames: api.beyond_plan_names,
        tokens: api.tokens,
        tokenByPhase: api.token_by_phase,
        tokenByAgentType: api.token_by_agent_type,
        agents: api.agents,
        tokenAccountingConsistent: api.token_accounting_consistent,
      } satisfies DastRunDetail;
    },
    enabled: Boolean(pipelineId),
    staleTime: Infinity,
  });
}

export type CweMeta = {
  title: string;
  description: string;
  impact: string;
  url: string;
};

export function useCweMeta(cweId: number | null | undefined) {
  return useQuery({
    queryKey: ["cwe-meta", cweId],
    queryFn: async () => {
      if (!cweId) return null;
      return fetchJson<CweMeta>(getRoute("cwe_detail_url", { cwe_id: cweId }));
    },
    enabled: Boolean(cweId),
    staleTime: 24 * 60 * 60 * 1000, // 24 h — CWE data is static
  });
}

export type FindingTagOptions = {
  names: string[];
  counts: Record<string, number>;
};

/** The tag vocabulary in scope. One hook, so every consumer of this cache key sees one shape. */
export function useFindingTagsByProject(projectId?: number) {
  return useQuery({
    queryKey: ["finding-tags", projectId ?? "all"],
    queryFn: async (): Promise<FindingTagOptions> => {
      const params = projectId ? `?project_id=${projectId}` : "";
      const payload = await fetchJson<{ tags: string[]; counts?: Record<string, number> }>(
        `${getRoute("finding_tags_url")}${params}`,
      );
      const cleaned = (payload.tags ?? []).map((tag) => tag.trim()).filter(Boolean);
      return { names: Array.from(new Set(cleaned)), counts: payload.counts ?? {} };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export type WorkItemProviderSummary = {
  id: number;
  name: string;
  providerType: string;
  providerTypeDisplay: string;
  baseUrl: string;
  syncEnabled: boolean;
  hasToken: boolean;
  vpnIntegrationId: number | null;
};

export function useWorkItems(findingId?: number) {
  return useQuery({
    queryKey: ["work-items", findingId],
    queryFn: async () => {
      if (!findingId) return [] as WorkItemLink[];
      const payload = await fetchJson<{ results?: WorkItemLinkApi[] } | WorkItemLinkApi[]>(
        getRoute("finding_work_items_url", { finding_id: findingId }),
      );
      const items = Array.isArray(payload) ? payload : (payload.results ?? []);
      return items.map((wi): WorkItemLink => ({
        id: wi.id,
        externalKey: wi.external_key,
        externalUrl: wi.external_url,
        title: wi.title,
        statusCategory: (wi.status_category as WorkItemLink["statusCategory"]) ?? "UNKNOWN",
        providerName: wi.provider_name ?? null,
        providerType: wi.provider_type ?? null,
        provider: wi.provider ?? null,
      }));
    },
    enabled: Boolean(findingId),
  });
}

type WorkItemProviderApi = {
  id: number;
  name: string;
  provider_type: string;
  provider_type_display: string;
  base_url?: string;
  sync_enabled: boolean;
  has_token: boolean;
  vpn_integration?: number | null;
};

export function useWorkItemProviders(orgId?: number) {
  return useQuery({
    queryKey: ["work-item-providers", orgId],
    queryFn: async () => {
      if (!orgId) return [] as WorkItemProviderSummary[];
      const resolved = getRoute("work_item_providers_url", { org_id: orgId });
      const payload = await fetchJson<{ results?: WorkItemProviderApi[] } | WorkItemProviderApi[]>(resolved);
      const items = Array.isArray(payload) ? payload : (payload.results ?? []);
      return items.map((p): WorkItemProviderSummary => ({
        id: p.id,
        name: p.name,
        providerType: p.provider_type,
        providerTypeDisplay: p.provider_type_display,
        baseUrl: p.base_url ?? "",
        syncEnabled: p.sync_enabled,
        hasToken: p.has_token,
        vpnIntegrationId: p.vpn_integration ?? null,
      }));
    },
    enabled: Boolean(orgId),
  });
}

export function useAiResponse(aiByFinding: Map<number, AIResponse>, findingId?: number): AIResponse | null {
  if (!findingId) return null;
  return aiByFinding.get(findingId) ?? null;
}

type CalendarEventsFilters = {
  start: string;
  end: string;
  view: CalendarView;
  eventTypes: CalendarEvent["eventType"][];
  projectIds?: number[];
  timezone?: string;
  grouping?: "auto" | "none";
  limit?: number;
};

export function useCalendarEvents(filters: CalendarEventsFilters) {
  return useQuery({
    queryKey: ["calendar-events", filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        start: filters.start,
        end: filters.end,
        view: filters.view,
        ...(filters.timezone ? { timezone: filters.timezone } : {}),
        ...(filters.grouping ? { grouping: filters.grouping } : {}),
        ...(filters.limit ? { limit: String(filters.limit) } : {}),
      });
      for (const eventType of filters.eventTypes) {
        params.append("event_types", eventType);
      }
      for (const projectId of filters.projectIds ?? []) {
        params.append("project_id", String(projectId));
      }
      const payload = await fetchJson<CalendarEventsApiResponse>(
        `${getRoute("calendar_events_url")}?${params.toString()}`,
      );
      return (payload.events ?? []).map((item): CalendarEvent => ({
        id: item.id,
        eventType: item.event_type,
        title: item.title,
        start: item.start,
        end: item.end ?? null,
        isAllDay: item.is_all_day,
        isAggregated: item.is_aggregated,
        count: item.count,
        isFuture: item.is_future,
        colorVariant: item.color_variant,
        summary: item.summary ?? {},
        link: item.link ?? null,
      }));
    },
    enabled: Boolean(filters.start && filters.end),
  });
}

export type DashboardSummary = {
  kpi: {
    total_active: number;
    critical_high: number;
    total_findings: number;
    risk_accepted: number;
    projects_count: number;
  };
  severity_distribution: Record<string, number>;
  top_projects: Array<{
    project_id: number | null;
    name: string;
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
    total_active: number;
  }>;
  finding_status_breakdown: {
    active: number;
    mitigated: number;
    risk_accepted: number;
    under_review: number;
    false_positive: number;
    out_of_scope: number;
  };
  findings_aging_heatmap: {
    buckets: string[];
    severities: string[];
    matrix: Record<string, Record<string, number>>;
  };
  risk_trend: Array<{
    week: string;
    new_findings: number;
    mitigated_findings: number;
    net: number;
  }>;
  pipeline_performance_trend: Array<{
    week: string;
    runs: number;
    median_duration_seconds: number;
    warnings_rate: number;
  }>;
  cwe_distribution: Array<{
    cwe: number;
    count: number;
    title: string;
    description: string;
    impact: string;
    url: string;
  }>;
  ai_verdict_analytics: {
    total: number;
    verdict_counts: Record<"true_positive" | "false_positive" | "uncertain", number>;
    severity_by_verdict: Record<
      "Critical" | "High" | "Medium" | "Low" | "Info",
      Record<"true_positive" | "false_positive" | "uncertain", number>
    >;
    uncertainty_buckets: Record<"low" | "medium" | "high", number>;
  };
  triage_progress: TriageProgress;
};

/** Work item state of a finding: ``none`` without tickets, else its deciding ticket's status. */
export type TriageState = "none" | WorkItemStatusCategory;

export type TriageProgress = {
  total_active: number;
  ticketed: number;
  coverage_pct: number;
  untriaged_critical_high: number;
  by_state: Array<{ state: TriageState; count: number }>;
  by_severity: Array<{
    severity: Severity;
    total: number;
    ticketed: number;
    ticketed_pct: number;
    untriaged_pct: number;
    target_pct: number;
    below_target: boolean;
  }>;
  ticketed_recently: {
    days: number;
    linked_from: string;
    count: number;
    previous_count: number;
    delta_pct: number | null;
  };
  stale_open: { days: number; linked_until: string; count: number };
  weekly: Array<{
    week: string;
    week_end: string;
    new_findings: number;
    ticketed: number;
    dismissed: number;
    untriaged_at_week_end: number;
  }>;
};

export function useDashboardSummary(filters: FindingsFilterUrlState) {
  const query = buildFindingsQuery(filters).toString();
  return useQuery({
    queryKey: ["dashboard-summary", query],
    queryFn: () => fetchJson<DashboardSummary>(`${getRoute("dashboard_summary_url")}${query ? `?${query}` : ""}`),
    staleTime: 5 * 60 * 1000,
    // A filter change keeps the previous charts on screen (dimmed) instead of a skeleton flash.
    placeholderData: keepPreviousData,
  });
}

export function useCalendarEventDetail(eventId?: string, projectId?: number, timezone?: string) {
  return useQuery({
    queryKey: ["calendar-event-detail", eventId, projectId, timezone],
    queryFn: async () => {
      if (!eventId) return null;
      const params = new URLSearchParams({
        ...(projectId ? { project_id: String(projectId) } : {}),
        ...(timezone ? { timezone } : {}),
      });
      const url = getRoute("calendar_event_detail_url", { event_id: eventId });
      const payload = await fetchJson<CalendarEventApi>(
        `${url}${params.toString() ? `?${params.toString()}` : ""}`,
      );
      return {
        id: payload.id,
        eventType: payload.event_type,
        title: payload.title,
        start: payload.start,
        end: payload.end ?? null,
        isAllDay: payload.is_all_day,
        isAggregated: payload.is_aggregated,
        count: payload.count,
        isFuture: payload.is_future,
        colorVariant: payload.color_variant,
        summary: payload.summary ?? {},
        link: payload.link ?? null,
      } satisfies CalendarEvent;
    },
    enabled: Boolean(eventId),
  });
}

export type RiskApprovalCurrent = {
  id: number;
  acceptedBy: string;
  expirationDate: string | null;
  reactivateExpired: boolean;
  decisionDetails: string;
  created: string;
};

export type RiskApprovalStatus = {
  enabled: boolean;
  current: RiskApprovalCurrent | null;
};

export function useRiskApprovalStatus(findingId?: number) {
  return useQuery({
    queryKey: ["risk-approval-status", findingId],
    queryFn: async () => {
      if (!findingId) return null;
      const data = await fetchJson<{
        enabled: boolean;
        current: {
          id: number;
          accepted_by: string;
          expiration_date: string | null;
          reactivate_expired: boolean;
          decision_details: string;
          created: string;
        } | null;
      }>(getRoute("finding_risk_approval_url", { finding_id: findingId }));
      return {
        enabled: data.enabled,
        current: data.current
          ? {
              id: data.current.id,
              acceptedBy: data.current.accepted_by,
              expirationDate: data.current.expiration_date,
              reactivateExpired: data.current.reactivate_expired,
              decisionDetails: data.current.decision_details,
              created: data.current.created,
            }
          : null,
      } satisfies RiskApprovalStatus;
    },
    enabled: Boolean(findingId),
  });
}

export function useFindingTimeline(findingId?: number) {
  return useQuery({
    queryKey: ["finding-timeline", findingId],
    queryFn: async () => {
      if (!findingId) return [];
      const now = new Date();
      const end = now;
      const start = new Date(now.getTime() - (FINDING_HISTORY_RANGE_DAYS * DAY_MS));
      const params = new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString(),
        finding_id: String(findingId),
        limit: String(FINDING_TIMELINE_LIMIT),
      });
      for (const eventType of FINDING_TIMELINE_EVENT_TYPES) {
        params.append("event_types", eventType);
      }
      const payload = await fetchJson<FindingTimelineApiResponse>(
        `${getRoute("finding_timeline_url")}?${params.toString()}`,
      );
      return (payload.events ?? []).map((event): FindingTimelineEvent => ({
        id: event.id,
        eventType: event.event_type,
        happenedAt: event.happened_at,
        findingId: event.finding_id,
        title: event.title,
        severity: event.severity,
        projectIds: event.project_ids ?? [],
        processedReason: event.processed_reason ?? null,
        owner: event.owner ?? null,
        details: event.details ?? null,
        link: event.link,
      }));
    },
    enabled: Boolean(findingId),
  });
}

export type ManageableOrg = {
  id: number;
  name: string;
};

export function useManageableOrgs() {
  return useQuery({
    queryKey: ["manageable-orgs"],
    queryFn: async () => {
      const payload = await fetchJson<ListResponse<ManageableOrg>>(getRoute("manageable_orgs_url"));
      return payload.results ?? [];
    },
  });
}

export type VpnSecretStatus = {
  has_ovpn_content: boolean;
  has_client_cert: boolean;
  has_client_key: boolean;
  has_username: boolean;
};

export type OrgIntegration = {
  id: number;
  organization: number;
  integration_type: string;
  integration_type_display: string;
  name: string;
  config: Record<string, unknown>;
  has_secret: boolean;
  vpn_secret?: VpnSecretStatus;
  vpn_integration?: number | null;
  is_active: boolean;
  created: string;
  updated: string;
  dast_state?: {
    validation_state: string;
    validation_error_code: string;
    contract_version: string;
    validated_at: string | null;
    capabilities_etag: string;
    capabilities_synced_at: string | null;
    sync_error_code: string;
    sync_status: DastSyncStatus;
  } | null;
};

/** Lifecycle of the latest DAST catalog sync, as reported by the backend. */
export type DastSyncStatus = "IDLE" | "PENDING" | "RUNNING" | "SUCCEEDED" | "FAILED" | "STALLED";

export function isDastSyncInFlight(integration: OrgIntegration): boolean {
  const status = integration.dast_state?.sync_status;
  return status === "PENDING" || status === "RUNNING";
}

export type ProjectIntegrationOverride = {
  id: number;
  project: number;
  integration_type: string;
  org_integration: number | null;
  config_override: Record<string, unknown>;
  is_disabled: boolean;
};

export function useProjectIntegrationOverrides(projectId?: number) {
  return useQuery({
    queryKey: ["project-integration-overrides", projectId],
    queryFn: async () => {
      if (!projectId) return [] as ProjectIntegrationOverride[];
      const payload = await fetchJson<ProjectIntegrationOverride[] | { results?: ProjectIntegrationOverride[] }>(
        getRoute("project_integration_overrides_url", { project_id: projectId }),
      );
      return Array.isArray(payload) ? payload : (payload.results ?? []);
    },
    enabled: Boolean(projectId),
  });
}

export type DastTarget = {
  id: number;
  provider_id: string;
  display_name: string;
  contract_revision: string;
  capability_revision: string;
  schema_digest: string;
  parameter_schema: Record<string, unknown>;
  provider_defaults: Record<string, unknown>;
  repository_keys: string[];
  launch_requirements: string[];
  autonomous_ready: boolean;
  is_available: boolean;
  last_seen_at: string;
};

export type DastProjectBinding = {
  id: number;
  project: number;
  target: DastTarget;
  source_repo_key: string;
  enabled: boolean;
  parameter_snapshot: Record<string, unknown>;
  readiness: {
    ready: boolean;
    issues: Array<{ code: string; detail: string }>;
    checked_at: string;
  };
  created: string;
  updated: string;
};

export function useOrganizationDastTargets(orgId?: number) {
  return useQuery({
    queryKey: ["dast-targets", orgId],
    queryFn: async () => {
      if (!orgId) return [] as DastTarget[];
      const payload = await fetchJson<DastTarget[] | { results?: DastTarget[] }>(
        getRoute("organization_dast_target_catalog_url", { org_id: orgId }),
      );
      return Array.isArray(payload) ? payload : (payload.results ?? []);
    },
    enabled: Boolean(orgId),
  });
}

export function useProjectDastBindings(projectId?: number) {
  return useQuery({
    queryKey: ["dast-bindings", projectId],
    queryFn: async () => {
      if (!projectId) return [] as DastProjectBinding[];
      const payload = await fetchJson<DastProjectBinding[] | { results?: DastProjectBinding[] }>(
        getRoute("project_dast_bindings_url", { project_id: projectId }),
      );
      return Array.isArray(payload) ? payload : (payload.results ?? []);
    },
    enabled: Boolean(projectId),
  });
}

export type ValidationTaskStatus = {
  state: "PENDING" | "STARTED" | "VALIDATING" | "READY" | "INVALID" | "SUCCESS" | "FAILURE";
  valid: boolean | null;
  detail: string;
};

export function useValidationStatus(integrationId: number | null, taskId: string | null) {
  return useQuery({
    queryKey: ["validate-status", integrationId, taskId],
    queryFn: () =>
      fetchJson<ValidationTaskStatus>(
        getRoute("org_integration_validate_status_url", {
          integration_id: integrationId!,
          task_id: taskId!,
        }),
      ),
    enabled: Boolean(integrationId && taskId),
    refetchInterval: (query) => {
      const s = query.state.data?.state;
      if (s === "SUCCESS" || s === "FAILURE" || s === "READY" || s === "INVALID") return false;
      return 2000;
    },
  });
}

const PIPELINE_TERMINAL_STATUSES = new Set(["FINISHED", "FINISHED_WITH_WARNINGS"]);

/** Polls a pipeline's status (e.g. while a DAST report import runs asynchronously). */
export function usePipelineStatus(pipelineId: string | null) {
  return useQuery({
    queryKey: ["pipeline-status", pipelineId],
    queryFn: () => fetchJson<PipelineApi>(getRoute("pipeline_detail_url", { pipeline_id: pipelineId! })),
    enabled: Boolean(pipelineId),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      if (s && PIPELINE_TERMINAL_STATUSES.has(s) && !query.state.data?.run_task_id) return false;
      return 2000;
    },
  });
}

export function useWorkItemProviderValidationStatus(providerId: number | null, taskId: string | null) {
  return useQuery({
    queryKey: ["wip-validate-status", providerId, taskId],
    queryFn: () =>
      fetchJson<ValidationTaskStatus>(
        getRoute("work_item_provider_validate_status_url", {
          provider_id: providerId!,
          task_id: taskId!,
        }),
      ),
    enabled: Boolean(providerId && taskId),
    refetchInterval: (query) => {
      const s = query.state.data?.state;
      if (s === "SUCCESS" || s === "FAILURE") return false;
      return 2000;
    },
  });
}

export function useOrgIntegrations(orgId?: number) {
  return useQuery({
    queryKey: ["org-integrations", orgId],
    queryFn: async () => {
      if (!orgId) return [] as OrgIntegration[];
      const payload = await fetchJson<OrgIntegration[] | { results?: OrgIntegration[] }>(
        getRoute("org_integrations_url", { org_id: orgId }),
      );
      return Array.isArray(payload) ? payload : (payload.results ?? []);
    },
    enabled: Boolean(orgId),
    // A DAST catalog sync runs in a worker; keep the list fresh until it finishes.
    refetchInterval: (query) => (query.state.data?.some(isDastSyncInFlight) ? 2000 : false),
  });
}

export type DuplicateCandidate = { id: number; title: string; severity: string };

/**
 * Search for findings that could serve as the "original" when closing a finding
 * as a duplicate.  Accepts either a numeric finding ID (exact match via detail
 * endpoint) or a title substring (icontains search via list endpoint).
 * The current finding (excludeId) is always excluded from results.
 */
export function useDuplicateCandidates(search: string, excludeId: number) {
  const trimmed = search.trim();
  const isNumeric = trimmed !== "" && /^\d+$/.test(trimmed);
  const numericId = isNumeric ? parseInt(trimmed, 10) : undefined;

  return useQuery<DuplicateCandidate[]>({
    queryKey: ["duplicate-candidates", trimmed, excludeId],
    queryFn: async () => {
      if (!trimmed) return [];

      const results: DuplicateCandidate[] = [];

      if (isNumeric && numericId !== undefined && numericId !== excludeId) {
        // Exact ID lookup via finding detail endpoint
        try {
          const data = await fetchJson<FindingApi>(getRoute("finding_detail_url", { id: numericId }));
          if (data?.id) {
            results.push({ id: data.id, title: data.title, severity: data.severity });
          }
        } catch {
          // 404 or not authorised — no match
        }
      }

      if (!isNumeric && trimmed.length >= 2) {
        // Title substring search
        const params = new URLSearchParams({ title: trimmed, limit: "8" });
        const data = await fetchJson<ListResponse<FindingApi>>(
          `${getRoute("findings_list_url")}?${params}`,
        );
        for (const f of data.results ?? []) {
          if (f.id !== excludeId && !results.some((r) => r.id === f.id)) {
            results.push({ id: f.id, title: f.title, severity: f.severity });
          }
        }
      }

      return results.slice(0, 7);
    },
    enabled: Boolean(trimmed),
    staleTime: 30_000,
  });
}
